/**
 * Rule-context allow-list of the claim detail page (`/sinistres/:idSin`):
 * the extranet base variables plus the claim's id, status, type, product and
 * responsibility rate (from `WS2_EA_FIC_SIN`). Registered as the
 * `contextVariables` of every claim detail card, so the admin config page and
 * the live `RuleContextProvider` stay in lockstep. The list page exposes the
 * base context only.
 */

import { useMemo } from 'react';

import type { ContextVariableDescriptor } from '@components/shared/configurable';

import type { ClaimRecord } from '../../api/claimsService';
import {
  extranetBaseContextSchema,
  useExtranetBaseRuleContext,
  type ExtranetBaseContextKey,
} from '../../contextVariables';

/** Variables of the claim detail page. */
export const claimDetailContextSchema = {
  ...extranetBaseContextSchema,
  claim_id: {
    type: 'string',
    description: 'Identifier of the claim shown (route parameter).',
    sourceField: 'ID_SIN',
  },
  claim_status: {
    type: 'string',
    description: 'Computed claim status: OUVERT or CLOS.',
    sourceField: 'ETAT',
  },
  claim_type: {
    type: 'string',
    description: 'Claim type as returned by VEOS (e.g. MATÉRIEL).',
    sourceField: 'TYPE_SIN',
  },
  claim_product: {
    type: 'string',
    description: 'Product label of the claim.',
    sourceField: 'LIB_PROD',
  },
  claim_responsibility: {
    type: 'string',
    description:
      'Responsibility rate: « Non déterminé », « 0% », « 50% », « 100% » or « Non renseigné ».',
    sourceField: 'TAUX_RESP',
  },
} as const satisfies Record<string, ContextVariableDescriptor>;

/** Literal union of every key in {@link claimDetailContextSchema}. */
export type ClaimDetailContextKey = keyof typeof claimDetailContextSchema;

/** Claim-specific keys (on top of the base ones). */
export type ClaimContextKey = Exclude<ClaimDetailContextKey, ExtranetBaseContextKey>;

/** Primitive inputs of {@link buildClaimRuleContext}. */
export interface ClaimRuleContextInput {
  idSin: string;
  status: string | null;
  type: string | null;
  product: string | null;
  responsibility: string | null;
}

/** The primitive inputs of a claim (record `null` / not loaded ⇒ only the id). */
export function claimRuleContextInputOf(
  idSin: string,
  record: ClaimRecord | null | undefined,
): ClaimRuleContextInput {
  return {
    idSin,
    status: record?.etat ?? null,
    type: record?.type ?? null,
    product: record?.produit ?? null,
    responsibility: record?.tauxResponsabilite ?? null,
  };
}

/** Claim-specific part of the context bag (`undefined` for an unknown value). */
export function buildClaimRuleContext(
  input: ClaimRuleContextInput,
): Record<ClaimContextKey, string | undefined> {
  return {
    claim_id: input.idSin === '' ? undefined : input.idSin,
    claim_status: input.status ?? undefined,
    claim_type: input.type ?? undefined,
    claim_product: input.product ?? undefined,
    claim_responsibility: input.responsibility ?? undefined,
  };
}

/** The claim detail rule-context bag, memoised on its primitive inputs. */
export function useClaimDetailRuleContext(
  idSin: string,
  record: ClaimRecord | null | undefined,
): Record<ClaimDetailContextKey, unknown> {
  const base = useExtranetBaseRuleContext();
  const { status, type, product, responsibility } = claimRuleContextInputOf(idSin, record);

  return useMemo(
    () => ({
      ...base,
      ...buildClaimRuleContext({ idSin, status, type, product, responsibility }),
    }),
    [base, idSin, status, type, product, responsibility],
  );
}
